import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_THEME, THEME_KEYS, type ThemeKey } from '@/styles/themes';
import {
  ThemeContext,
  type ResolvedMode,
  type ThemeContextValue,
  type ThemeMode,
} from './theme-context';

// Keep these in sync with the inline FOUC-guard script in index.html.
const STORAGE_KEY_MODE = 'fluentix.preferredMode';
const STORAGE_KEY_THEME = 'fluentix.preferredTheme';

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY_MODE);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function readStoredTheme(): ThemeKey {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const raw = window.localStorage.getItem(STORAGE_KEY_THEME);
  if (raw && (THEME_KEYS as string[]).includes(raw)) return raw as ThemeKey;
  return DEFAULT_THEME;
}

function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode === 'system') {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  defaultTheme?: ThemeKey;
}

export function ThemeProvider({
  children,
  defaultMode,
  defaultTheme,
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(
    () => defaultMode ?? readStoredMode(),
  );
  const [theme, setThemeState] = useState<ThemeKey>(
    () => defaultTheme ?? readStoredTheme(),
  );
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() =>
    resolveMode(mode),
  );

  // Apply tokens to <html> and, when in 'system' mode, react live to OS changes.
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const next = resolveMode(mode);
      setResolvedMode(next);
      root.classList.toggle('dark', next === 'dark');
      root.dataset.theme = theme;
      root.style.colorScheme = next;
    };
    apply();
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
    };
  }, [mode, theme]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    window.localStorage.setItem(STORAGE_KEY_MODE, next);
  }, []);

  const setTheme = useCallback((next: ThemeKey) => {
    setThemeState(next);
    window.localStorage.setItem(STORAGE_KEY_THEME, next);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(resolvedMode === 'dark' ? 'light' : 'dark');
  }, [resolvedMode, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedMode, theme, setMode, setTheme, toggleMode }),
    [mode, resolvedMode, theme, setMode, setTheme, toggleMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
